import { post, get } from '../api.js';
import { generateRegistrationKeys, deriveAndStoreKeys, checkHIBP, generateBip39Mnemonic, deriveRecoveryKeys, wrapMasterKeyForRecovery, wipe } from '../crypto.js';
import { navigate } from '../router.js';
import { lucideIcons, esc } from '../app.js';
import { setToken, getPostAuthRedirect, clearPostAuthRedirect } from '../auth-store.js';

export function renderSetupPassword(container) {
  let mnemonic = null;
  let savedPassword = null;

  renderStep1();

  function renderStep1() {
    container.innerHTML = `
      <div class="login-card">
        <div class="logo">
          <svg viewBox="0 0 32 32" width="48" height="48"><defs><linearGradient id="bk-setup-pw" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#3ecfb4"/><stop offset="100%" stop-color="#2a9d8f"/></linearGradient></defs><rect width="32" height="32" rx="7" fill="url(#bk-setup-pw)"/><rect x="10" y="14" width="12" height="10" rx="2" fill="#0c0e14"/><path d="M12.5 14V11a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="#0c0e14" stroke-width="2.2" stroke-linecap="round"/><circle cx="16" cy="19" r="1.5" fill="url(#bk-setup-pw)"/><line x1="16" y1="20.5" x2="16" y2="22" stroke="url(#bk-setup-pw)" stroke-width="1.5" stroke-linecap="round"/></svg>
          <div class="brand">Blind<span>Keep</span></div>
        </div>
        <p style="color:var(--text-secondary);margin-bottom:1.5rem;text-align:center">
          Set a passphrase to protect your encryption keys. This cannot be recovered without your recovery words.
        </p>
        <div id="setup-msg"></div>
        <form id="setup-form">
          <div class="field"><label>Passphrase</label><input type="password" name="password" required minlength="16" placeholder="e.g. correct horse battery staple"></div>
          <p style="font-size:12px;color:var(--text-secondary);margin:-4px 0 8px">Use 4+ random words. Minimum 16 characters.</p>
          <div class="field"><label>Confirm Passphrase</label><input type="password" name="confirm" required></div>
          <div id="hibp-warning" style="display:none">
            <label style="display:flex;align-items:center;gap:8px;font-size:13px;color:var(--text-secondary);margin-bottom:8px">
              <input type="checkbox" id="accept-risk"> Breach check unavailable. I accept the risk.
            </label>
          </div>
          <button type="submit" class="btn btn-primary" style="margin-top:8px">Continue</button>
        </form>
      </div>
    `;
    lucideIcons();

    document.getElementById('setup-form').onsubmit = async (e) => {
      e.preventDefault();
      const msg = document.getElementById('setup-msg');
      const btn = e.target.querySelector('button');
      const password = e.target.password.value;
      const confirm = e.target.confirm.value;
      msg.innerHTML = '';

      if (password.length < 16) {
        msg.innerHTML = '<div class="msg msg-error">Passphrase must be at least 16 characters.</div>';
        return;
      }
      if (password !== confirm) {
        msg.innerHTML = '<div class="msg msg-error">Passphrases do not match.</div>';
        return;
      }

      btn.disabled = true;
      btn.textContent = 'Checking...';

      const hibp = await checkHIBP(password);
      if (hibp.breached) {
        msg.innerHTML = `<div class="msg msg-error">This password appeared in ${hibp.count.toLocaleString()} data breaches. Choose a different one.</div>`;
        btn.disabled = false;
        btn.textContent = 'Continue';
        return;
      }
      if (hibp.error) {
        const warning = document.getElementById('hibp-warning');
        warning.style.display = '';
        const checkbox = document.getElementById('accept-risk');
        if (!checkbox.checked) {
          msg.innerHTML = '<div class="msg msg-warning">Could not check breach database. Check the box below to proceed anyway.</div>';
          btn.disabled = false;
          btn.textContent = 'Continue';
          return;
        }
      }

      savedPassword = password;
      mnemonic = await generateBip39Mnemonic();
      renderStep2();
    };
  }

  function renderStep2() {
    const words = mnemonic.split(' ');
    const grid = words.map((w, i) => `<div class="recovery-word"><span class="recovery-num">${i + 1}.</span> ${esc(w)}</div>`).join('');

    container.innerHTML = `
      <div class="login-card" style="max-width:520px">
        <div class="logo">
          <svg viewBox="0 0 32 32" width="48" height="48"><defs><linearGradient id="bk-setup-pw" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#3ecfb4"/><stop offset="100%" stop-color="#2a9d8f"/></linearGradient></defs><rect width="32" height="32" rx="7" fill="url(#bk-setup-pw)"/><rect x="10" y="14" width="12" height="10" rx="2" fill="#0c0e14"/><path d="M12.5 14V11a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="#0c0e14" stroke-width="2.2" stroke-linecap="round"/><circle cx="16" cy="19" r="1.5" fill="url(#bk-setup-pw)"/><line x1="16" y1="20.5" x2="16" y2="22" stroke="url(#bk-setup-pw)" stroke-width="1.5" stroke-linecap="round"/></svg>
          <div class="brand">Recovery Words</div>
        </div>
        <p style="color:var(--text-secondary);margin-bottom:1rem;text-align:center">
          Write these 12 words down and store them safely. They are the <strong>only way</strong> to recover your vault if you forget your passphrase.
        </p>
        <div class="recovery-grid">${grid}</div>
        <p style="font-size:12px;color:var(--text-secondary);margin-top:12px;text-align:center">
          Write these words down on paper. Do not store them digitally.
        </p>
        <label style="display:flex;align-items:center;gap:8px;margin-top:16px;font-size:14px;color:var(--text-secondary);cursor:pointer">
          <input type="checkbox" id="written-down-check"> I have written down my recovery words
        </label>
        <button class="btn btn-primary" style="margin-top:12px;width:100%" id="saved-btn" disabled>Continue</button>
      </div>
    `;
    lucideIcons();

    document.getElementById('written-down-check').onchange = (e) => {
      document.getElementById('saved-btn').disabled = !e.target.checked;
    };

    document.getElementById('saved-btn').onclick = () => renderStep3();
  }

  function renderStep3() {
    const words = mnemonic.split(' ');
    const positions = [];
    while (positions.length < 3) {
      const p = Math.floor(Math.random() * 12);
      if (!positions.includes(p)) positions.push(p);
    }
    positions.sort((a, b) => a - b);

    container.innerHTML = `
      <div class="login-card">
        <div class="logo">
          <svg viewBox="0 0 32 32" width="48" height="48"><defs><linearGradient id="bk-setup-pw" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#3ecfb4"/><stop offset="100%" stop-color="#2a9d8f"/></linearGradient></defs><rect width="32" height="32" rx="7" fill="url(#bk-setup-pw)"/><rect x="10" y="14" width="12" height="10" rx="2" fill="#0c0e14"/><path d="M12.5 14V11a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="#0c0e14" stroke-width="2.2" stroke-linecap="round"/><circle cx="16" cy="19" r="1.5" fill="url(#bk-setup-pw)"/><line x1="16" y1="20.5" x2="16" y2="22" stroke="url(#bk-setup-pw)" stroke-width="1.5" stroke-linecap="round"/></svg>
          <div class="brand">Confirm Words</div>
        </div>
        <p style="color:var(--text-secondary);margin-bottom:1.5rem;text-align:center">
          Enter the requested recovery words to confirm you saved them.
        </p>
        <div id="confirm-msg"></div>
        <form id="confirm-form">
          ${positions.map(p => `<div class="field"><label>Word #${p + 1}</label><input type="text" name="word${p}" required autocomplete="off" autocapitalize="none"></div>`).join('')}
          <button type="submit" class="btn btn-primary" style="margin-top:8px">Complete Setup</button>
        </form>
      </div>
    `;
    lucideIcons();

    document.getElementById('confirm-form').onsubmit = async (e) => {
      e.preventDefault();
      const msg = document.getElementById('confirm-msg');
      const btn = e.target.querySelector('button');
      msg.innerHTML = '';

      for (const p of positions) {
        const input = e.target[`word${p}`].value.trim().toLowerCase();
        if (input !== words[p]) {
          msg.innerHTML = '<div class="msg msg-error">One or more words are incorrect. Please try again.</div>';
          return;
        }
      }

      btn.disabled = true;
      btn.textContent = 'Setting up...';

      try {
        const keys = generateRegistrationKeys(savedPassword);
        const authKeyHex = Array.from(keys.auth_key).map(b => b.toString(16).padStart(2, '0')).join('');

        const recovery = await deriveRecoveryKeys(mnemonic);
        const recoveryWrapped = wrapMasterKeyForRecovery(recovery.wrappingKey);
        const recoveryAuthHex = Array.from(recovery.authKey).map(b => b.toString(16).padStart(2, '0')).join('');
        wipe(recovery.wrappingKey, recovery.authKey);

        const auth = await post('/auth/set-password', {
          auth_key: authKeyHex,
          public_key: keys.public_key,
          encrypted_private_key: keys.encrypted_private_key,
          client_salt: keys.client_salt,
          encrypted_master_key: keys.encrypted_master_key,
          recovery_wrapped_master_key: Array.from(recoveryWrapped),
          recovery_auth_key: recoveryAuthHex,
        });
        setToken(auth.token);

        const me = await get('/auth/me');
        deriveAndStoreKeys(savedPassword, me.client_salt, me.encrypted_private_key, me.public_key, me.encrypted_master_key);

        savedPassword = null;
        mnemonic = null;

        const redirect = getPostAuthRedirect();
        clearPostAuthRedirect();
        navigate(redirect || '#/items');
      } catch (err) {
        msg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
        btn.disabled = false;
        btn.textContent = 'Complete Setup';
      }
    };
  }
}
